import React, { useMemo, useState, useCallback } from "react";
import { Button, Card, Switch, Spin } from "antd";
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
  Customized,
} from "recharts";
import styles from "./styles.module.scss";
import InfoIcon from "@assets/icons/i.svg";
import { useFinanceChartData } from "./hooks/useFinanceChartData";
import { CustomTooltip } from "@pages/Dashboard/features/SalesPerformance/Chart/CustomTooltip";
import { Wave } from "@components/ui-kit/Wave";
import { useIsMobile } from "src/utils/isMobile";
import { modifyTooltipPayload } from "@components/SalesPerformance/utils";
import { FinanceFlowProps } from "./types";

const colorMapping = {
  amazon_ad_sales: "#80C67A",
  amazon_organic_sales: "#1BB08D",
  tiktok_sales: "#20976B",
  shopify_sales: "#146D4C",
  amazon_cogs: "#FE5858",
  tiktok_cogs: "#5295E0",
  shopify_cogs: "#FA8484",
  amazon_fees: "#F19867",
  ad_spend: "#FCB38B",
  other_fees: "#F167B0",
  shopify_cost: "#ff6e8c",
  tiktok_cost: "#E7429A",
  amazon_opex: "#A78AF5",
  tiktok_opex: "#8969DB",
  shopify_opex: "#6B4DBA",
  cash_flow: "#2ec4b6",
};

const nameMapping = {
  amazon_ad_sales: "Amazon Ad Sales",
  amazon_organic_sales: "Amazon Organic Sales",
  tiktok_sales: "TikTok Sales",
  shopify_sales: "Shopify Sales",
  amazon_cogs: "Amazon COGS",
  tiktok_cogs: "TikTok COGS",
  shopify_cogs: "Shopify COGS",
  amazon_fees: "Amazon Fees",
  ad_spend: "Ad Spend",
  other_fees: "Other Fees",
  shopify_cost: "Shopify Cost",
  tiktok_cost: "TikTok Cost",
  amazon_opex: "Amazon OPEX",
  tiktok_opex: "TikTok OPEX",
  shopify_opex: "Shopify OPEX",
  cash_flow: "Cash Flow",
};

const FinanceFlowChart: React.FC<FinanceFlowProps> = ({
  showForecast,
  title = false,
  dataPoints,
  timeRange,
  asin,
  dateRange,
}) => {
  const isControlled = showForecast !== undefined;
  const [showForecastInternal, setShowForecastInternal] =
    useState<boolean>(false);

  const effectiveShowForecast = isControlled
    ? showForecast
    : showForecastInternal;

  const { chartData, periods, isLoading, error } = useFinanceChartData({
    timeRange,
    effectiveShowForecast,
    dataPoints,
    asin,
    dateRange,
  });

  const formatPeriod = useCallback(
    (period: string, idx: number) =>
      period.includes(" ") ? period : `${idx + 1} ${period}`,
    [
      // no dependencies
    ],
  );

  const transformedChartData = useMemo(() => {
    if (!chartData || !periods?.length) return [];

    return periods.map((period, idx) => {
      const formatted = formatPeriod(period, idx);
      const row: any = { period: formatted };

      Object.entries(dataPoints || {}).forEach(([key, enabled]) => {
        if (!enabled) return;
        const value = chartData[key]?.[idx];
        if (value === undefined) return;
        row[key] = value;
        row[`${key}_main`] = value;
        if (effectiveShowForecast) {
          row[`${key}_forecast`] = value * 1.1;
        }
      });

      return row;
    });
  }, [chartData, periods, dataPoints, effectiveShowForecast, formatPeriod]);

  const numberOfBars = useMemo(
    () => transformedChartData?.length || 0,
    [transformedChartData],
  );

  const formatTick = useCallback(
    (value: string) => {
      if (value.includes(" ")) return value;
      const unit = timeRange === "month" ? "Month" : "Week";
      return `${value} ${unit}`;
    },
    [timeRange],
  );

  const currentLegendColorMapping = useMemo(() => colorMapping, []);
  const currentNameMapping = useMemo(() => nameMapping, []);

  const isMobile = useIsMobile(1200);
  const chartMargins = useMemo(
    () =>
      isMobile
        ? { top: 20, right: 30, left: -20, bottom: 5 }
        : { top: 20, right: 30, left: 50, bottom: 5 },
    [isMobile],
  );

  const renderTooltip = ({ active, payload, label }: any) => {
    const modified = modifyTooltipPayload(payload);
    return (
      <CustomTooltip
        active={active}
        payload={modified}
        colorMetric={currentLegendColorMapping}
        label={label}
      />
    );
  };

  const renderBars = () => {
    if (!dataPoints) return null;
    return Object.keys(dataPoints)
      .filter((key) => dataPoints[key])
      .map((key) => (
        <React.Fragment key={key}>
          <Bar
            dataKey={`${key}_main`}
            stackId="a"
            fill={currentLegendColorMapping[key] ?? "#8884d8"}
            name={currentNameMapping[key] ?? key.replace(/_/g, " ")}
          />
          {effectiveShowForecast && (
            <Bar
              dataKey={`${key}_forecast`}
              stackId="a"
              fill={currentLegendColorMapping[key] ?? "#8884d8"}
              fillOpacity={0.5}
              stroke={currentLegendColorMapping[key] ?? "#8884d8"}
              strokeDasharray="5 5"
              strokeWidth={2}
            />
          )}
        </React.Fragment>
      ));
  };

  const renderWave = ({ width, height }: any) => {
    const { left, right, top, bottom } = chartMargins;
    const plotWidth = width - left - right;
    return (
      <svg width={width} height={height}>
        <Wave
          width={plotWidth}
          height={height - top - bottom}
          numberOfBars={numberOfBars}
          shiftPx={110}
          cumulativeStart={-5000}
          yDomain={[-10000, 10000]}
        />
      </svg>
    );
  };

  const yDomain = useMemo(() => {
    if (!transformedChartData.length) {
      return [-10000, 10000];
    }
    let min = 0;
    let max = 0;
    transformedChartData.forEach((item) => {
      Object.values(item).forEach((val) => {
        if (typeof val === "number") {
          min = Math.min(min, val);
          max = Math.max(max, val);
        }
      });
    });
    return [Math.floor(min * 1.1), Math.ceil(max * 1.1)];
  }, [transformedChartData]);

  if (isLoading) {
    return (
      <Card className={styles.card}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "400px",
          }}
        >
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className={styles.card}>
        <div style={{ padding: "20px", color: "red" }}>
          Error loading chart data: {error}
        </div>
      </Card>
    );
  }

  return (
    <Card className={styles.card}>
      <div className={styles.headerBetween}>
        <h1 className={styles.title}>{title ? "Cash Flow" : "Finance Flow"}</h1>
      </div>

      <div className={styles.rowLegend}>
        <div className={styles.legendContainer}>
          {dataPoints &&
            Object.keys(dataPoints)
              .filter((key) => dataPoints[key])
              .map((key) => {
                const hasKey = transformedChartData.some((row) => key in row);
                if (!hasKey) return null;
                return (
                  <div className={styles.legendItem} key={key}>
                    <span
                      className={styles.colorDot}
                      style={{
                        backgroundColor: currentLegendColorMapping[key],
                      }}
                    />
                    <span className={styles.legendText}>
                      {currentNameMapping[key]}
                    </span>
                  </div>
                );
              })}

          {!effectiveShowForecast && !isControlled && (
            <div className={styles.legendItem}>
              <span
                className={styles.colorDot}
                style={{ backgroundColor: "#2ec4b6" }}
              />
              <span className={styles.legendText}>Cumulative SF</span>
            </div>
          )}
        </div>

        {!isControlled && (
          <div className={styles.switchRow}>
            <div className={styles.switchContainer}>
              <Switch
                checked={showForecastInternal}
                onChange={setShowForecastInternal}
              />
              <span className={styles.switchLabel}>Show Forecast</span>
            </div>

            <div className={styles.switchContainer}>
              <Switch />
              <span className={styles.switchLabel}>Show Past Data</span>
            </div>

            <Button
              type="link"
              className={styles.infoButton}
              icon={
                <img
                  src={InfoIcon}
                  alt="Info"
                  className={styles.infoIcon}
                  width={14}
                  height={14}
                />
              }
            />
          </div>
        )}
      </div>

      <div className={styles.chartContainer}>
        <ResponsiveContainer width="100%" height={520}>
          <ComposedChart
            data={transformedChartData}
            margin={chartMargins}
            stackOffset="sign"
          >
            <CartesianGrid stroke="#1F1F20" vertical={false} />
            <XAxis
              dataKey="period"
              tick={{ fill: "white" }}
              axisLine={{ stroke: "#1F1F20" }}
              tickFormatter={formatTick}
            />
            <YAxis
              tick={{ fill: "white" }}
              axisLine={{ stroke: "#1F1F20" }}
              domain={yDomain}
              tickFormatter={(value) => `$${Math.abs(value) / 1000}K`}
            />
            <Tooltip content={renderTooltip} />
            {renderBars()}
            <Customized component={renderWave} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </Card>
  );
};

export default FinanceFlowChart;
